import { useId } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import {
  CurrencyStringCents,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'
import { QuoteRating } from '@b-stock/shipment-api-client'

import {
  getServiceLevel,
  type ParcelRateGroup as Group,
} from './groupParcelRates'
import { ParcelRateCard, RateBadge } from './ParcelRateCard'

const ns = 'ParcelRateList'

const Container = styled.div`
  border: 0.0625rem solid ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const Header = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
`

const CarrierName = styled.span`
  ${Typography.Subtitle2}
  color: ${Colors.Semantic.Neutral.black};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

// FedEx's brand colors, drawn as text rather than an image.
const FedExLogo = styled.span`
  ${Typography.Body4}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 5rem;
  height: 2.25rem;
  border-radius: 0.375rem;
  background-color: #4d148c;
  color: #ffffff;
  font-weight: bold;
  letter-spacing: 0.04em;
`

const FedExLogoEx = styled.span`
  color: #ff6600;
`

const HeaderDetail = styled.span`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
`

const Rates = styled.div`
  padding: 0 1rem 1rem;
`

type ParcelRateGroupProps = {
  group: Group
  selectedQuoteId: string | undefined
  isOpen: boolean
  onToggle: () => void
  showCheapestBadge: boolean
  currency: string
  disabled: boolean
  onSelect: (quoteId: string) => void
}

export const ParcelRateGroup = ({
  group,
  selectedQuoteId,
  isOpen,
  onToggle,
  showCheapestBadge,
  currency,
  disabled,
  onSelect,
}: ParcelRateGroupProps) => {
  const ratesId = useId()
  const cheapestPrice = group.quotes[0]?.totalPrice ?? 0
  const selected = group.quotes.find((q) => q._id === selectedQuoteId)
  const hasCheapestRate = group.quotes.some(
    (q) => q.rating === QuoteRating.Cheapest
  )

  return (
    <Container data-testid="parcel-rate-group">
      <Header
        type="button"
        aria-expanded={isOpen}
        aria-controls={ratesId}
        onClick={onToggle}
      >
        <CarrierName>
          {group.carrierName === 'FedEx' && (
            <FedExLogo aria-hidden data-testid="parcel-carrier-logo">
              Fed<FedExLogoEx>Ex</FedExLogoEx>
            </FedExLogo>
          )}
          {group.carrierName}
          {showCheapestBadge && hasCheapestRate && (
            <RateBadge>
              <FormattedMessage id={`${ns}.badge.cheapest`} />
            </RateBadge>
          )}
        </CarrierName>
        <HeaderDetail>
          {selected ? (
            <FormattedMessage
              id={`${ns}.group.selected`}
              values={{
                service: getServiceLevel(selected) ?? '',
                price: (
                  <CurrencyStringCents
                    value={selected.totalPrice}
                    currency={currency}
                  />
                ),
              }}
            />
          ) : (
            <FormattedMessage
              id={`${ns}.group.options`}
              values={{
                price: (
                  <CurrencyStringCents
                    value={cheapestPrice}
                    currency={currency}
                  />
                ),
                count: group.quotes.length,
              }}
            />
          )}
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            size="sm"
          />
        </HeaderDetail>
      </Header>
      {isOpen && (
        <Rates id={ratesId}>
          {group.quotes.map((quote, index) => (
            <ParcelRateCard
              key={quote._id ?? index}
              quote={quote}
              isSelected={
                quote._id !== undefined && quote._id === selectedQuoteId
              }
              currency={currency}
              disabled={disabled}
              onSelect={onSelect}
            />
          ))}
        </Rates>
      )}
    </Container>
  )
}
